package com.gastoncantera.intercom.call

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import kotlinx.serialization.Serializable

const val STATUS_RINGING = "ringing"
const val STATUS_CONNECTED = "connected"
const val STATUS_ENDED = "ended"

/** SDP serializable (type = "offer"/"answer"). */
@Serializable
data class SessionDescriptionData(
    val type: String = "",
    val sdp: String = "",
)

/** ICE candidate serializable. Campos alineados con webrtc-kmp. */
@Serializable
data class IceCandidateData(
    val sdpMid: String = "",
    val sdpMLineIndex: Int = 0,
    val candidate: String = "",
)

/** Documento de la llamada en `calls/{callId}`. */
@Serializable
data class CallSession(
    val caller: String = "",
    val callee: String = "",
    val status: String = STATUS_RINGING,
    val offer: SessionDescriptionData? = null,
    val answer: SessionDescriptionData? = null,
)

/**
 * Signaling 1:1 sobre Firestore.
 *
 * ```
 * calls/{callId}                 ← CallSession (offer/answer/status)
 *   ├─ callerCandidates/{auto}   ← IceCandidateData del que llama
 *   └─ calleeCandidates/{auto}   ← IceCandidateData del que recibe
 * ```
 */
class CallRepository {
    private val calls = Firebase.firestore.collection("calls")

    private fun callDoc(callId: String) = calls.document(callId)
    private fun callerCandidates(callId: String) = callDoc(callId).collection("callerCandidates")
    private fun calleeCandidates(callId: String) = callDoc(callId).collection("calleeCandidates")

    // -------- Caller --------

    /** Crea/reinicia la llamada con la offer. Limpia candidatos viejos. */
    suspend fun startCall(callId: String, caller: String, callee: String, offer: SessionDescriptionData) {
        clearCandidates(callId)
        callDoc(callId).set(
            CallSession(
                caller = caller,
                callee = callee,
                status = STATUS_RINGING,
                offer = offer,
                answer = null,
            )
        )
    }

    suspend fun addCallerCandidate(callId: String, candidate: IceCandidateData) {
        callerCandidates(callId).add(candidate)
    }

    fun observeCalleeCandidates(callId: String): Flow<List<IceCandidateData>> =
        calleeCandidates(callId).snapshots.map { snap ->
            snap.documents.map { it.data(IceCandidateData.serializer()) }
        }

    // -------- Callee --------

    /**
     * Claim atómico de la answer: solo el primero gana.
     * @return false si ya había answer (otro device atendió) o el doc no existe.
     */
    suspend fun tryAnswerCall(callId: String, answer: SessionDescriptionData): Boolean {
        val ref = callDoc(callId)
        return Firebase.firestore.runTransaction {
            val snap = get(ref)
            if (!snap.exists) return@runTransaction false
            val session = snap.data(CallSession.serializer())
            if (session.answer != null) return@runTransaction false
            set(
                ref,
                CallSession.serializer(),
                session.copy(status = STATUS_CONNECTED, answer = answer),
            )
            true
        }
    }

    suspend fun addCalleeCandidate(callId: String, candidate: IceCandidateData) {
        calleeCandidates(callId).add(candidate)
    }

    fun observeCallerCandidates(callId: String): Flow<List<IceCandidateData>> =
        callerCandidates(callId).snapshots.map { snap ->
            snap.documents.map { it.data(IceCandidateData.serializer()) }
        }

    // -------- Compartido --------

    /** Estado completo de la llamada (null si no existe = sin llamada / terminada). */
    fun observeCall(callId: String): Flow<CallSession?> =
        callDoc(callId).snapshots.map { snap ->
            if (snap.exists) snap.data(CallSession.serializer()) else null
        }

    /** Termina la llamada: borra candidatos y el doc. El otro lado lo ve como null. */
    suspend fun endCall(callId: String) {
        clearCandidates(callId)
        callDoc(callId).delete()
    }

    private suspend fun clearCandidates(callId: String) {
        listOf(callerCandidates(callId), calleeCandidates(callId)).forEach { col ->
            col.get().documents.forEach { it.reference.delete() }
        }
    }
}
